import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { RiskGrossEditSheet } from '../../../-components/RiskGrossEditSheet';
import { RiskGrossHistorySheet } from '../../../-components/RiskGrossHistorySheet';
import { RiskGrossTable } from '../../../-components/RiskGrossTable';
import {
  allAdminRisksQueryOptions,
  riskGrossQueryOptions,
  useAllAdminRisks,
} from '../../../-hooks/useRiskGross';
import { useRiskGrossTab } from '../../../-hooks/useRiskGrossTab';

const searchSchema = z.object({
  page: z.number().optional(),
  limit: z.number().optional(),
  sort: z.string().optional(),
  filters: z.record(z.string(), z.array(z.string())).optional(),
  search: z.string().optional(),
});

export const Route = createFileRoute(
  '/management/risk-management/(pages)/(list)/risk-gross/',
)({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  /**
   * The rows, and the risk catalogue alongside them: it names every row and
   * fills the risk filter, so fetching it here avoids a second wait.
   *
   * Rejections are swallowed — the loader is a prefetch, and letting it throw
   * hands the route to the router's error boundary, which replaces the page and
   * hides the table's own failure state.
   */
  loader: ({ deps }) =>
    Promise.all([
      queryClient.ensureQueryData(riskGrossQueryOptions(deps)),
      queryClient.ensureQueryData(allAdminRisksQueryOptions()),
    ]).catch(() => undefined),
  component: RiskGrossTabPage,
});

function RiskGrossTabPage() {
  const { getTranslation: t } = useTranslator({
    page: 'managementRiskManagement',
  });
  const tab = useRiskGrossTab(t);
  const { risks } = useAllAdminRisks();

  return (
    <PageContainer>
      <RiskGrossTable
        rows={tab.list.rows}
        risks={risks}
        total={tab.list.total}
        isLoading={tab.list.isLoading}
        sorting={tab.list.sorting}
        filters={tab.list.filters}
        pagination={tab.list.pagination}
        onSortingChange={tab.list.onSortingChange}
        onFiltersChange={tab.list.onFiltersChange}
        onPaginationChange={tab.list.onPaginationChange}
        onCreate={tab.openCreate}
        onEdit={tab.openEdit}
        onViewHistory={tab.openHistory}
      />
      <RiskGrossEditSheet
        open={tab.editing.open}
        riskGross={tab.editing.row}
        risks={risks}
        isSaving={tab.isSaving}
        onOpenChange={(open: boolean) => !open && tab.closeEdit()}
        onSubmit={tab.handleSubmit}
      />
      <RiskGrossHistorySheet
        open={tab.history.open}
        riskGross={tab.history.row}
        rows={tab.historyRows}
        isLoading={tab.historyLoading}
        onOpenChange={(open: boolean) => !open && tab.closeHistory()}
      />
    </PageContainer>
  );
}
